/**
 * Where an administrator lands: the company, rather than a worklist.
 *
 * Three sections, each a month of the business. An owner sees every active
 * company at once and a company admin sees their own, and that difference is
 * decided server-side by role — there is no company_id on any of these
 * requests for somebody to swap. One page, two audiences, one scope rule.
 *
 * **The reference layout has a fourth tab, Bench & Marketing, and it is not
 * here.** That product is bench sales: consultants sitting on bench, marketed
 * to vendors by BSRs. This one is agency recruitment, placing candidates into
 * client roles. There is no bench, no marketer, no vendor and no
 * consultant-on-bench state anywhere in the schema — the word appears twice in
 * the codebase, once as a *junk* client name the importer discards. A tab
 * would have to invent the business model to have anything to show, so it is
 * left out rather than rendered empty.
 *
 * **Counts, never conversion rates.** Said once here and once on the server:
 * the book holds thousands of submissions against a dozen offers, which as a
 * percentage reads like a verdict on the desk when what it reports is that two
 * stages are barely recorded. The four funnel stages are also dated on their
 * own events rather than followed as a cohort, so they do not subtract to give
 * a drop-off even in principle.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  IndianRupee,
  Loader2,
  Phone,
  Send,
  TrendingUp,
  UserCheck,
  Users,
} from 'lucide-react';
import { client } from '../../api/client';
import { money } from '../../lib/money';
import { titleCaseName } from '../../lib/titleCase';
import { canSeeAllCompanies, useAuth } from '../../store/auth';
import DashboardShell from '../../components/DashboardShell';
import type {
  AdminPipeline,
  AdminTeamPerformance,
  Overview,
} from '../../types';

const SECTIONS = ['Overview', 'Sales & Pipeline', 'Team Performance'] as const;
type Section = (typeof SECTIONS)[number];

/** The month a page opened on, which is the one in progress. */
function thisMonth(): { year: number; month: number } {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/* ------------------------------------------------------------------ */
/* Presentation                                                        */
/* ------------------------------------------------------------------ */

function Tile({
  label,
  value,
  icon: Icon,
  hint,
}: {
  label: string;
  value: string;
  icon: typeof Phone;
  hint?: string;
}) {
  return (
    <div
      className="group rounded-xl bg-white p-4 ring-1 ring-gray-200
                 transition-shadow duration-300 ease-out hover:shadow-lg
                 hover:shadow-gray-300/50 hover:ring-gray-300"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm text-gray-500">{label}</p>
          <p className="mt-2 text-3xl font-semibold tabular-nums text-gray-900">
            {value}
          </p>
        </div>
        <span
          className="grid h-10 w-10 shrink-0 place-items-center rounded-lg
                     bg-indigo-50 text-indigo-600 transition-transform
                     duration-300 ease-out motion-safe:group-hover:scale-110"
        >
          <Icon className="h-5 w-5" />
        </span>
      </div>
      {hint && <p className="mt-1 text-[11px] text-gray-400">{hint}</p>}
    </div>
  );
}

function Panel({
  title,
  hint,
  children,
  className = '',
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`flex flex-col rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200
                  transition-shadow duration-300 ease-out hover:shadow-md ${className}`}
    >
      <div className="mb-3">
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="py-6 text-center text-sm text-gray-500">{children}</p>;
}

function Spinner() {
  return (
    <div className="flex justify-center py-10 text-gray-400">
      <Loader2 className="h-5 w-5 animate-spin" />
    </div>
  );
}

/**
 * A labelled bar, sized against the largest row rather than against a total.
 *
 * Against the total, a stage holding almost everything renders every other bar
 * as a sliver — which is exactly the shape this data has. The number is always
 * printed beside it, so the bar is an aid to scanning and never the only place
 * the figure appears.
 */
function Bar({
  label,
  count,
  largest,
  tone = 'indigo',
}: {
  label: string;
  count: number;
  largest: number;
  tone?: 'indigo' | 'gray';
}) {
  const width = largest > 0 ? Math.max((count / largest) * 100, count > 0 ? 2 : 0) : 0;
  return (
    <li className="py-1.5">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="min-w-0 truncate text-gray-700">{label}</span>
        <span className="shrink-0 font-medium tabular-nums text-gray-900">
          {count.toLocaleString('en-IN')}
        </span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-100">
        <div
          className={`h-full rounded-full ${
            tone === 'indigo' ? 'bg-indigo-500' : 'bg-gray-400'
          }`}
          style={{ width: `${width}%` }}
        />
      </div>
    </li>
  );
}

/** Statuses arrive as enum values; this is the only place they are prettified. */
function humanise(value: string): string {
  return value
    .replace(/_/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());
}

/* ------------------------------------------------------------------ */
/* Sections                                                            */
/* ------------------------------------------------------------------ */

function OverviewSection({ year, month }: { year: number; month: number }) {
  const user = useAuth((s) => s.user);
  const owner = canSeeAllCompanies(user);

  const { data, isLoading } = useQuery<Overview>({
    queryKey: ['admin-overview', year, month],
    queryFn: async () =>
      (await client.get('/api/v1/admin/overview', {
        params: { year, month },
        // The scope is the caller's role. Narrowing by acting company as well
        // would hide the very comparison this screen exists for.
        skipActingCompany: true,
      })).data,
  });

  if (isLoading) return <Spinner />;
  if (!data) return <Empty>Nothing to show for this month.</Empty>;

  const t = data.totals;
  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Tile label="Calls" value={t.calls.toLocaleString('en-IN')} icon={Phone} />
        <Tile
          label="Submissions"
          value={t.submissions.toLocaleString('en-IN')}
          icon={Send}
        />
        <Tile
          label="Placements"
          value={t.placements.toLocaleString('en-IN')}
          icon={UserCheck}
          hint="Counted on the joining date"
        />
        <Tile
          label="Placement billing"
          value={money(t.placementBilling)}
          icon={IndianRupee}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <Tile
          label="Active employees"
          value={t.activeEmployeeCount.toLocaleString('en-IN')}
          icon={Users}
        />
        <Tile label="Payroll, net" value={money(t.payrollNet)} icon={IndianRupee} />
        <Tile
          label="Payroll, employer cost"
          value={money(t.payrollEmployerCost)}
          icon={IndianRupee}
        />
      </div>

      {/* The per-company table, which is the owner's whole reason for being
          here. A company admin sees exactly one row, so the table would be a
          heading over a restatement of the tiles above. */}
      {owner && (
        <Panel
          title="By company"
          hint="Active companies only. A switched-off company is not one having a quiet month."
        >
          {data.companies.length === 0 ? (
            <Empty>No active companies.</Empty>
          ) : (
            <div className="table-scroll rounded-lg ring-1 ring-gray-200">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-gray-200 bg-gray-50 text-xs text-gray-500">
                  <tr>
                    <th className="px-3 py-2.5 font-medium">Company</th>
                    <th className="px-3 py-2.5 text-right font-medium">Staff</th>
                    <th className="px-3 py-2.5 text-right font-medium">Calls</th>
                    <th className="px-3 py-2.5 text-right font-medium">Submissions</th>
                    <th className="px-3 py-2.5 text-right font-medium">Placements</th>
                    <th className="px-3 py-2.5 text-right font-medium">Billing</th>
                    <th className="px-3 py-2.5 text-right font-medium">Payroll</th>
                  </tr>
                </thead>
                <tbody>
                  {data.companies.map((c) => (
                    <tr key={c.companyId} className="border-t border-gray-100">
                      <td className="px-3 py-2">
                        <span className="font-medium text-gray-900">
                          {c.companyName}
                        </span>
                        {(c.city || c.state) && (
                          <span className="block text-[11px] text-gray-400">
                            {[c.city, c.state].filter(Boolean).join(', ')}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                        {c.activeEmployeeCount}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                        {c.calls.toLocaleString('en-IN')}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                        {c.submissions.toLocaleString('en-IN')}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                        {c.placements}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                        {money(c.placementBilling)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                        {money(c.payrollNet)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}

function PipelineSection({ year, month }: { year: number; month: number }) {
  const { data, isLoading } = useQuery<AdminPipeline>({
    queryKey: ['admin-pipeline', year, month],
    queryFn: async () =>
      (await client.get('/api/v1/admin/pipeline', {
        params: { year, month },
        skipActingCompany: true,
      })).data,
  });

  if (isLoading) return <Spinner />;
  if (!data) return <Empty>Nothing to show for this month.</Empty>;

  const largestStage = Math.max(...data.funnel.map((s) => s.count), 0);
  const largestStatus = Math.max(
    ...data.submissionStatuses.map((s) => s.count),
    0,
  );
  const largestClient = Math.max(...data.topClients.map((c) => c.submissions), 0);

  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {data.funnel.map((stage) => (
          <Tile
            key={stage.stage}
            label={stage.stage}
            value={stage.count.toLocaleString('en-IN')}
            icon={TrendingUp}
          />
        ))}
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <Panel
          title="Pipeline"
          hint="Each stage counted on its own date, so these are four months of activity rather than one cohort followed through."
        >
          {largestStage === 0 ? (
            <Empty>Nothing recorded this month.</Empty>
          ) : (
            <ul>
              {data.funnel.map((s) => (
                <Bar
                  key={s.stage}
                  label={s.stage}
                  count={s.count}
                  largest={largestStage}
                />
              ))}
            </ul>
          )}
        </Panel>

        <Panel
          title="Submissions by status"
          hint="Where this month's submissions stand now."
        >
          {data.submissionStatuses.length === 0 ? (
            <Empty>No submissions this month.</Empty>
          ) : (
            <ul>
              {data.submissionStatuses.map((s) => (
                <Bar
                  key={s.status}
                  label={humanise(s.status)}
                  count={s.count}
                  largest={largestStatus}
                />
              ))}
            </ul>
          )}
        </Panel>

        <Panel
          title="Placements by status"
          hint="Among those joining this month."
        >
          {data.placementStatuses.length === 0 ? (
            <Empty>Nobody joined this month.</Empty>
          ) : (
            <ul>
              {data.placementStatuses.map((s) => (
                <Bar
                  key={s.status}
                  label={humanise(s.status)}
                  count={s.count}
                  largest={Math.max(
                    ...data.placementStatuses.map((x) => x.count),
                  )}
                  tone="gray"
                />
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <Panel
          className="xl:col-span-2"
          title="Where submissions went"
          hint="This month's busiest clients."
        >
          {data.topClients.length === 0 ? (
            <Empty>No submissions this month.</Empty>
          ) : (
            <ul>
              {data.topClients.map((c) => (
                <Bar
                  key={c.clientId}
                  label={c.clientName}
                  count={c.submissions}
                  largest={largestClient}
                />
              ))}
            </ul>
          )}
        </Panel>

        <Panel
          title="Open requirements"
          hint="Standing, not monthly — what is waiting rather than what happened."
        >
          <p className="text-4xl font-semibold tabular-nums text-gray-900">
            {data.openRequirements.toLocaleString('en-IN')}
          </p>
        </Panel>
      </div>
    </div>
  );
}

function TeamSection({ year, month }: { year: number; month: number }) {
  const user = useAuth((s) => s.user);
  const owner = canSeeAllCompanies(user);

  const { data, isLoading } = useQuery<AdminTeamPerformance>({
    queryKey: ['admin-team-performance', year, month],
    queryFn: async () =>
      (await client.get('/api/v1/admin/team-performance', {
        params: { year, month },
        skipActingCompany: true,
      })).data,
  });

  if (isLoading) return <Spinner />;
  if (!data || data.recruiters.length === 0) {
    return (
      <Panel title="Team performance">
        <Empty>Nobody logged anything this month.</Empty>
      </Panel>
    );
  }

  return (
    <Panel
      title="Team performance"
      hint="Busiest first. Only people with something recorded this month appear."
    >
      <div className="table-scroll rounded-lg ring-1 ring-gray-200">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-gray-200 bg-gray-50 text-xs text-gray-500">
            <tr>
              <th className="px-3 py-2.5 font-medium">Recruiter</th>
              {owner && <th className="px-3 py-2.5 font-medium">Company</th>}
              <th className="px-3 py-2.5 text-right font-medium">Calls</th>
              <th className="px-3 py-2.5 text-right font-medium">Submissions</th>
              <th className="px-3 py-2.5 text-right font-medium">Placements</th>
              <th className="px-3 py-2.5 text-right font-medium">Billing</th>
            </tr>
          </thead>
          <tbody>
            {data.recruiters.map((r) => (
              <tr key={r.userId} className="border-t border-gray-100">
                <td className="px-3 py-2 font-medium text-gray-900">
                  {titleCaseName(r.fullName)}
                </td>
                {owner && (
                  <td className="px-3 py-2 text-gray-500">
                    {r.companyName ?? '—'}
                  </td>
                )}
                <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                  {r.calls.toLocaleString('en-IN')}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                  {r.submissions.toLocaleString('en-IN')}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                  {r.placements}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                  {money(r.placementBilling)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/* The page                                                            */
/* ------------------------------------------------------------------ */

export default function AdminHomePage() {
  const user = useAuth((s) => s.user);
  const [section, setSection] = useState<Section>('Overview');
  const [{ year, month }, setPeriod] = useState(thisMonth);

  const shiftMonth = (by: number) => {
    setPeriod((p) => {
      const d = new Date(p.year, p.month - 1 + by, 1);
      return { year: d.getFullYear(), month: d.getMonth() + 1 };
    });
  };

  return (
    <DashboardShell>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Dashboard</h1>
            <p className="mt-0.5 text-sm text-gray-500">
              {canSeeAllCompanies(user)
                ? 'Every active company, one month at a time.'
                : 'Your company, one month at a time.'}
            </p>
          </div>

          {/* One month, moved a month at a time. A free date range would be a
              wider promise than the figures keep: every stage here is counted
              on its own date, and payroll exists per month or not at all. */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              className="btn-secondary text-xs"
              onClick={() => shiftMonth(-1)}
            >
              Previous
            </button>
            <span className="min-w-[9.5rem] text-center text-sm font-medium text-gray-900">
              {MONTH_NAMES[month - 1]} {year}
            </span>
            <button
              type="button"
              className="btn-secondary text-xs"
              onClick={() => shiftMonth(1)}
            >
              Next
            </button>
          </div>
        </div>

        {/* Three, not the reference's four. Bench & Marketing is absent because
            the business it measures does not exist here -- see the note at the
            top of this file. */}
        <nav className="flex flex-wrap gap-1 border-b border-gray-200">
          {SECTIONS.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => setSection(name)}
              className={`border-b-2 px-3 py-2.5 text-sm font-medium transition-colors
                          duration-300 ease-out ${
                            section === name
                              ? 'border-indigo-600 text-indigo-600'
                              : 'border-transparent text-gray-500 hover:text-gray-900'
                          }`}
            >
              {name}
            </button>
          ))}
        </nav>

        {section === 'Overview' && <OverviewSection year={year} month={month} />}
        {section === 'Sales & Pipeline' && (
          <PipelineSection year={year} month={month} />
        )}
        {section === 'Team Performance' && <TeamSection year={year} month={month} />}

        {/* The one thing the reference has that this cannot: a standing note
            rather than a broken tab. Kept short and kept once. */}
        {section === 'Overview' && (
          <p className="flex items-start gap-2 rounded-lg bg-gray-50 px-3 py-2 text-[11px] leading-relaxed text-gray-500">
            <Building2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Figures are for the month shown. Placements and their billing are
              counted on the joining date, which is the event that earns the
              fee.
            </span>
          </p>
        )}
    </DashboardShell>
  );
}
